import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getPagesBySlug } from "../../services/apiService";
import useSlug from "../SlugComp";
import SEO from "../Seo";
import parse from "html-react-parser";

// Blog content map - can be expanded with more blog posts
const blogContentMap = {
  "it-staffing-companies-in-hyderabad": {
    id: 1,
    title: "IT staffing companies in hyderabad ",
    category: "Staffing",
    image:
      "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200",
    date: "August 19, 2026",
    author: "TechProjects",
    readTime: "10 minutes",
    excerpt:
      "IT staffing companies in Hyderabad offering skilled tech talent, flexible staffing solutions, and reliable workforce support. Choose Tech Projects today.",
    content: [
      {
        type: "heading2",
        text: "IT Staff Augmentation vs. Managed Services: Which Is Better for Your Business?",
      },
      {
        type: "heading3",
        text: "Introduction",
      },
      {
        type: "paragraph",
        text: "Businesses today need to scale technology teams quickly while controlling costs and maintaining productivity. Two popular approaches are IT staff augmentation and managed services.",
      },
      {
        type: "paragraph",
        text: "Although both models provide access to external technology expertise, they work differently. IT staff augmentation adds skilled professionals to your existing team, while managed services transfer responsibility for specific IT functions to an external provider.",
      },
      {
        type: "paragraph",
        text: "So, which model is right for your business?",
      },
       {
        type: "paragraph",
        text: "This guide explains the key differences, benefits, use cases, costs, and factors to consider before choosing between the two.",
      },
      {
  type: "heading2",
  text: "What Is IT Staff Augmentation?",
},
{
  type: "paragraph",
  text: 'IT staff augmentation is a flexible workforce model that allows businesses to add skilled technology professionals to their existing teams for a specific project or period.',
},
{
  type: "paragraph",
  text: 'Instead of hiring permanent employees, companies can work with an <a href="https://techprojects.com/Services/SWS" class="text-blue-600 hover:text-blue-800 font-semibold">IT staffing company in Hyderabad</a> to find professionals with the required technical expertise.',
},
{
  type: "paragraph",
  text: "Common roles include:",
},
{
  type: "bulletList",
  items: [
    "Software developers",
    "Cloud engineers",
    "DevOps engineers",
    "Data engineers",
    "Cybersecurity specialists",
    "QA engineers",
    "Business analysts",
    "Project managers"
  ],
},

{
  type: "heading3",
  text: "How IT Staff Augmentation Works",
},
{
  type: "paragraph",
  text: "The process is straightforward:",
},
{
  type: "numberedList",
  items: [
    "Identify the required skills and roles.",
    "Define the project duration and requirements.",
    "Work with an IT staffing provider to identify suitable professionals.",
    "Select and onboard the required resources.",
    "Manage the augmented professionals as part of your internal team."
  ],
},
{
  type: "paragraph",
  text: "This model gives businesses greater control over project execution while providing access to specialized talent.",
},

{
  type: "heading2",
  text: "What Are Managed Services?",
},
{
  type: "paragraph",
  text: "Managed services involve outsourcing specific IT operations or functions to an external technology provider.",
},
{
  type: "paragraph",
  text: "Instead of adding individual professionals to your existing team, the service provider takes responsibility for delivering and managing an agreed service.",
},
{
  type: "paragraph",
  text: "Common managed services include:",
},
{
  type: "bulletList",
  items: [
    "Cloud management",
    "Cybersecurity",
    "Network management",
    "Infrastructure management",
    "Application support",
    "IT help desk",
    "Security monitoring",
    "Data management"
  ],
},
{
  type: "paragraph",
  text: "Managed services are particularly useful for organizations that want reliable ongoing IT support without managing every operational function internally.",
},
{
  type: "heading2",
  text: "IT Staff Augmentation vs. Managed Services: Key Differences",
},
{
  type: "table",
  headers: [
    "Factor",
    "IT Staff Augmentation",
    "Managed Services"
  ],
  rows: [
    [
      "Primary objective",
      "Add skilled professionals",
      "Outsource IT functions"
    ],
    [
      "Management",
      "Client-managed",
      "Provider-managed"
    ],
    [
      "Control",
      "High",
      "Shared/provider-led"
    ],
    [
      "Flexibility",
      "Very high",
      "Based on service agreement"
    ],
    [
      "Best suited for",
      "Projects and skill gaps",
      "Ongoing IT operations"
    ],
    [
      "Resources",
      "Individual professionals",
      "Dedicated service/team"
    ],
    [
      "Scalability",
      "Easy to increase or reduce resources",
      "Depends on service scope"
    ],
    [
      "Responsibility",
      "Client retains project responsibility",
      "Provider takes service responsibility"
    ]
  ],
},
{
  type: "paragraph",
  text: "The simplest way to understand the difference is:",
},
{
  type: "paragraph",
  text: "Staff augmentation provides the talent. Managed services provide the service and operational responsibility.",
},
{
  type: "heading2",
  text: "Benefits of IT Staff Augmentation",
},
{
  type: "heading3",
  text: "1. Access to Specialized IT Talent",
},
{
  type: "paragraph",
  text: "Businesses can quickly access professionals with specific technical skills without conducting a lengthy permanent hiring process.",
},
{
  type: "heading3",
  text: "2. Greater Project Control",
},
{
  type: "paragraph",
  text: "Your internal managers retain control over project priorities, workflows, deadlines, and technical decisions.",
},
{
  type: "heading3",
  text: "3. Flexible Workforce Scaling",
},
{
  type: "paragraph",
  text: "You can increase or reduce your technology workforce based on project requirements.",
},
{
  type: "heading3",
  text: "4. Faster Hiring",
},
{
  type: "paragraph",
  text: "Working with an experienced IT staffing provider can help businesses identify suitable candidates faster.",
},
{
  type: "heading3",
  text: "5. Reduced Long-Term Hiring Commitment",
},
{
  type: "paragraph",
  text: "Staff augmentation can be useful when you need technical professionals for a specific project or limited period.",
},

{
  type: "heading2",
  text: "Benefits of Managed Services",
},
{
  type: "heading3",
  text: "1. Ongoing IT Support",
},
{
  type: "paragraph",
  text: "Managed services provide continuous support for critical technology functions.",
},
{
  type: "heading3",
  text: "2. Access to Specialized Expertise",
},
{
  type: "paragraph",
  text: "Businesses can access teams with experience in cloud, cybersecurity, infrastructure, applications, and other technology areas.",
},
{
  type: "heading3",
  text: "3. Reduced Operational Burden",
},
{
  type: "paragraph",
  text: "Your internal employees can focus on strategic initiatives while the service provider manages defined IT operations.",
},
{
  type: "heading3",
  text: "4. Predictable Service Delivery",
},
{
  type: "paragraph",
  text: "Managed service agreements can establish defined responsibilities, service levels, and performance expectations.",
},
{
  type: "heading3",
  text: "5. 24/7 Monitoring and Support",
},
{
  type: "paragraph",
  text: "Certain managed services can provide continuous monitoring and operational support, which is particularly valuable for critical IT environments.",
},

{
  type: "heading2",
  text: "When Should You Choose IT Staff Augmentation?",
},
{
  type: "paragraph",
  text: "IT staff augmentation is a strong option when your business:",
},
{
  type: "bulletList",
  items: [
    "Has an existing IT team",
    "Needs additional technical expertise",
    "Has a temporary skills gap",
    "Is launching a new technology project",
    "Needs to scale a development team",
    "Requires specialized professionals",
    "Wants greater control over project management"
  ],
},
{
  type: "paragraph",
  text: "For example, a company implementing a new cloud platform may need additional cloud engineers and DevOps professionals for six months. Staff augmentation allows the company to add these specialists while keeping project management internally.",
},

{
  type: "heading2",
  text: "When Should You Choose Managed Services?",
},
{
  type: "paragraph",
  text: "Managed services may be the better option when your business:",
},
{
  type: "bulletList",
  items: [
    "Needs continuous IT support",
    "Wants to outsource a complete IT function",
    "Requires 24/7 monitoring",
    "Needs managed cybersecurity",
    "Requires cloud or infrastructure management",
    "Has limited internal IT resources",
    "Wants to reduce operational complexity"
  ],
},
{
  type: "paragraph",
  text: "For example, a business may outsource its cybersecurity monitoring to a managed security provider while its internal team focuses on business applications and strategic technology initiatives.",
},

{
  type: "heading2",
  text: "IT Staffing Company in Hyderabad: Why Businesses Use Staff Augmentation",
},
{
  type: "paragraph",
  text: "Hyderabad has developed into a major technology and enterprise services hub, creating strong demand for skilled IT professionals.",
},
{
  type: "paragraph",
  text: "Businesses looking for an IT staffing company in Hyderabad can use staff augmentation to access professionals in areas such as:",
},
{
  type: "bulletList",
  items: [
    "Software development",
    "Cloud computing",
    "Artificial intelligence",
    "Data engineering",
    "Cybersecurity",
    "DevOps",
    "ERP",
    "Quality assurance"
  ],
},
{
  type: "paragraph",
  text: "For companies that already have an internal technology team but need additional expertise, staff augmentation can provide a practical way to scale without committing to permanent hiring for every position.",
},
{
  type: "paragraph",
  text: '<a href="https://techprojects.com/" class="text-blue-600 hover:text-blue-800 font-semibold">TechProjects</a> provides staffing and workforce solutions designed to help organizations meet contract, full-time, and project-based technology requirements.',
},

{
  type: "heading2",
  text: "IT Staffing NJ: Supporting Businesses in New Jersey",
},
{
  type: "paragraph",
  text: "New Jersey businesses also require specialized technology talent to support digital transformation, application development, cybersecurity, cloud migration, and other technology initiatives.",
},
{
  type: "paragraph",
  text: "An IT staffing NJ solution can help businesses:",
},
{
  type: "bulletList",
  items: [
    "Access specialized IT professionals",
    "Fill temporary skill gaps",
    "Scale project teams",
    "Support long-term technology initiatives",
    "Reduce recruitment complexity"
  ],
},
{
  type: "paragraph",
  text: "TechProjects combines its U.S. presence with India-based technology capabilities, enabling businesses to explore flexible workforce and technology delivery models.",
},

{
  type: "heading2",
  text: "Can You Combine Staff Augmentation and Managed Services?",
},
{
  type: "paragraph",
  text: "Yes. In many cases, a hybrid approach can provide the best balance between flexibility and operational efficiency.",
},
{
  type: "paragraph",
  text: "For example:",
},
{
  type: "heading3",
  text: "IT Staff Augmentation",
},
{
  type: "paragraph",
  text: "A company can add:",
},
{
  type: "bulletList",
  items: [
    "Software developers",
    "QA engineers",
    "Cloud engineers",
    "Project managers"
  ],
},
{
  type: "heading3",
  text: "Managed Services",
},
{
  type: "paragraph",
  text: "At the same time, the company can outsource:",
},
{
  type: "bulletList",
  items: [
    "Cloud monitoring",
    "Cybersecurity",
    "Infrastructure management",
    "Application support"
  ],
},
{
  type: "paragraph",
  text: "This approach allows the internal team to maintain control over strategic projects while an external provider handles selected operational responsibilities.",
},

{
  type: "heading2",
  text: "IT Staff Augmentation vs. Managed Services: Which Should You Choose?",
},
{
  type: "paragraph",
  text: "The right choice depends on your business requirements.",
},
{
  type: "heading3",
  text: "Choose IT Staff Augmentation If:",
},
{
  type: "paragraph",
  text: "You need people, skills, and flexibility while maintaining control over your projects.",
},
{
  type: "heading3",
  text: "Choose Managed Services If:",
},
{
  type: "paragraph",
  text: "You need an external provider to take responsibility for an ongoing IT function or service.",
},
{
  type: "heading3",
  text: "Choose a Hybrid Model If:",
},
{
  type: "paragraph",
  text: "You need additional technology professionals while also requiring ongoing support for specific IT operations.",
},

{
  type: "heading2",
  text: "Why Choose TechProjects for IT Staffing?",
},
{
  type: "paragraph",
  text: "TechProjects provides technology and workforce solutions for organizations looking to build, scale, and manage technology capabilities.",
},
{
  type: "paragraph",
  text: "Its capabilities include:",
},
{
  type: "bulletList",
  items: [
    "IT staffing",
    "Contract staffing",
    "Full-time staffing",
    "Project-based staffing",
    "Digital transformation",
    "Cybersecurity",
    "Cloud solutions",
    "Platform engineering",
    "Global Capability Center solutions"
  ],
},
{
  type: "paragraph",
  text: "For organizations searching for an IT staffing company in Hyderabad, IT staffing NJ, or flexible IT staffing solutions, TechProjects can support workforce requirements alongside broader technology initiatives.",
},

{
  type: "heading2",
  text: "Frequently Asked Questions",
},
{
  type: "heading3",
  text: "What is the difference between IT staff augmentation and managed services?",
},
{
  type: "paragraph",
  text: "IT staff augmentation adds skilled professionals to your existing team, while managed services outsource responsibility for specific IT functions or services.",
},
{
  type: "heading3",
  text: "Which is better for short-term IT projects?",
},
{
  type: "paragraph",
  text: "IT staff augmentation is generally more suitable for short-term projects because businesses can add specialized professionals for a defined period.",
},
{
  type: "heading3",
  text: "Is staff augmentation cheaper than managed services?",
},
{
  type: "paragraph",
  text: "The cost depends on the project, skills required, duration, and service scope. Staff augmentation is often suitable when you need specific professionals, while managed services are designed around ongoing service delivery.",
},
{
  type: "heading3",
  text: "How can I choose an IT staffing company in Hyderabad?",
},
{
  type: "paragraph",
  text: "Evaluate the provider's technical expertise, talent pool, recruitment process, scalability, industry experience, staffing models, and ability to understand your technology requirements.",
},
{
  type: "heading3",
  text: "What does IT staffing NJ mean?",
},
{
  type: "paragraph",
  text: "IT staffing NJ refers to IT recruitment and workforce solutions serving businesses in New Jersey, including contract, permanent, and project-based staffing.",
},
{
  type: "heading3",
  text: "Can TechProjects provide IT staffing solutions?",
},
{
  type: "paragraph",
  text: "Yes. TechProjects provides staffing and workforce solutions for contract, full-time, and project-based technology requirements, alongside broader enterprise technology services.",
},

{
  type: "heading2",
  text: "Conclusion",
},
{
  type: "paragraph",
  text: "IT staff augmentation and managed services serve different business needs. Staff augmentation is ideal when you need specialized professionals, flexibility, and direct project control. Managed services are better when you want an external provider to manage an ongoing IT function.",
},
{
  type: "paragraph",
  text: "For businesses that need both workforce flexibility and technology expertise, a hybrid approach can provide a practical solution.",
},
{
  type: "paragraph",
  text: "Whether you are looking for an IT staffing company in Hyderabad, IT staffing NJ, or broader technology workforce solutions, TechProjects can help you identify a suitable model based on your business goals, technical requirements, and growth plans.",
},
      

    ],
    relatedArticles: [
      {
        title: "Cloud Migration Best Practices",
        image:
          "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400",
        category: "Cloud",
        slug: "cloud-migration-best-practices",
      },
      {
        title: "React Performance Optimization",
        image:
          "https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=400",
        category: "React",
        slug: "react-performance-optimization",
      },
      {
        title: "DevOps Automation with CI/CD",
        image:
          "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=400",
        category: "DevOps",
        slug: "devops-automation-cicd",
      },
    ],
  },
};

function BlogDetail() {
  let slug = useSlug();
  const [metaData, setMetaData] = useState({});
  // useEffect(() => {
  //   const fetchMetaData = async () => {
  //     const data = await getPagesBySlug(slug);
  //     setMetaData(data?.meta || {});
  //   };
  //   fetchMetaData();
  // }, [slug]);
  // slug = useParams();
 useEffect(() => {
  const fetchData = async () => {
      const data = await getPagesBySlug(slug.toLowerCase());
      setMetaData(data.data);
    };

      fetchData();},[slug]);
  // console.log(slug+ "test")
  const navigate = useNavigate();
  const blog = blogContentMap["it-staffing-companies-in-hyderabad"];

  if (!blog) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Blog post not found
          </h2>
          <p className="text-gray-600 mb-8">
            The article you're looking for doesn't exist.
          </p>
          <button
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors duration-200"
            onClick={() => navigate("/blogs")}
          >
            Back to Blog
          </button>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    return blog.content.map((section, index) => {
      switch (section.type) {
        case "heading2":
          return (
            <h2
              key={index}
              className="text-3xl font-bold text-gray-900 mt-8 mb-4"
            >
              {parse(section.text)}
            </h2>
          );
        case "heading3":
          return (
            <h3
              key={index}
              className="text-2xl font-bold text-blue-600 mt-6 mb-3"
            >
              {parse(section.text)}
            </h3>
          );
        case "paragraph":
          return (
            <p key={index} className="text-gray-700 leading-relaxed mb-4">
              {parse(section.text)}
            </p>
          );
        case "bulletList":
          return (
            <ul key={index} className="list-disc list-inside space-y-2 mb-4 text-gray-700">
              {section.items.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          );
          case "table":
  return (
    <div key={index} className="overflow-x-auto mb-6">
      <table className="min-w-full border border-gray-300">
        <thead className="bg-gray-100">
          <tr>
            {section.headers.map((header, i) => (
              <th
                key={i}
                className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {section.rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className="border border-gray-300 px-4 py-3 text-gray-700"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
        default:
          return null;
      }
    });
  };

  return (
    <>
      {/* Hero Section with Image */}
      <section
        className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center justify-center text-white"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('${blog.image}')`,
        }}
      >
        <div className="text-center max-w-3xl px-4">
          <span className="inline-block bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
            {blog.category}
          </span>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {blog.title}
          </h1>
          <p className="text-lg text-gray-100">
            Cybersecurity compliance companies in Hyderabad help businesses meet ISO 27001, NIST, GDPR, DORA and other security standards with expert compliance services.
          </p>
        </div>
      </section>

      {/* Article Content */}
      <SEO {...metaData} />
      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Article Meta */}
          <div className="mb-8 pb-8 border-b border-gray-200 flex justify-between items-center flex-wrap gap-4">
            <div>
              <p className="text-gray-600 mb-1">By {blog.author}</p>
              <p className="text-gray-600">{blog.date}</p>
            </div>
            <div>
              <span className="text-sm text-gray-600">
                Estimated read time: {blog.readTime}
              </span>
            </div>
          </div>

          {/* Article Body */}
          <div className="prose prose-lg max-w-none">
            {renderContent()}

            {/* Call to Action */}
            {/* <div className="mt-8 p-6 bg-blue-50 border-l-4 border-blue-600 rounded-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Ready to Transform Your Development Process?
              </h3>
              <p className="text-gray-700 mb-4">
                TechProjects specializes in AI-driven software solutions and
                digital transformation. Our experts can help you integrate AI
                into your development workflows efficiently and securely.
              </p>
              <button
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors duration-200"
                onClick={() => {
                  window.location.href = "/Contact";
                }}
              >
                Get Started Today
              </button>
            </div> */}
          </div>

          {/* Share Section */}
          <div className="mt-8 pt-8 border-t border-gray-200 flex justify-between items-center flex-wrap gap-4">
            <div>
              <p className="text-gray-700 font-semibold">Share this article:</p>
            </div>
            <div className="flex gap-3">
              <button className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-200">
                Facebook
              </button>
              <button className="bg-blue-400 hover:bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-200">
                Twitter
              </button>
              <button className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-200">
                LinkedIn
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Related Articles */}
      {/* <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Related Articles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blog.relatedArticles.map((article, index) => (
              <div
                key={index}
                className="group bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer"
                onClick={() => navigate(`/blog/${article.slug}`)}
              >
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="p-6">
                  <span className="inline-block bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-xs font-semibold mb-3">
                    {article.category}
                  </span>
                  <h3 className="font-bold text-gray-900">{article.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Back to Blog Button */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-full transition-colors duration-200"
            onClick={() => navigate("/blogs")}
          >
            ← Back to Blog
          </button>
        </div>
      </section>
    </>
  );
}

export default BlogDetail;
